import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { SectionHeading } from '~/components/SectionHeading'
import { useArtwork } from '~/api/artwork'
import { api } from '~/api/client'
import type { Night, Profile as Person } from '~/api/types'
import { AppLink } from '~/components/AppLink'
import { bubbleFor } from '~/components/bubbles'
import { Avatar, AvatarPicker } from '~/social/Avatar'
import { Friends } from '~/social/Friends'

import s from './Profile.module.css'

/** London, and formatted here for the same reason every other date on this site is. */
const WHEN = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'Europe/London',
})

function nightsBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T12:00:00Z`)
  const b = Date.parse(`${to}T12:00:00Z`)
  return Math.round((a - b) / 86_400_000)
}

/** "tonight", "tomorrow", "in 9 nights" — a countdown reads better than a date here. */
function howSoon(night: string, today: string): string {
  const away = nightsBetween(night, today)
  if (away <= 0) return 'tonight'
  if (away === 1) return 'tomorrow'
  return `in ${away} nights`
}

/**
 * The longest run of consecutive nights out.
 *
 * On the nights, not on the dates — two gigs on the same night are one night here, which is
 * the same thing every count on this page means by it. A festival day billed by three feeds
 * as three rows would otherwise read as a three-day streak.
 */
function longestRun(nights: Night[]): number {
  const days = [...new Set(nights.map(n => n.night))].sort()
  let best = 0
  let run = 0
  let previous: string | null = null
  for (const day of days) {
    run = previous !== null && nightsBetween(day, previous) === 1 ? run + 1 : 1
    previous = day
    best = Math.max(best, run)
  }
  return best
}

const TIME = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Europe/London',
})

function tally(nights: Night[], pick: (n: Night) => string): [string, number][] {
  const counts = new Map<string, number>()
  for (const night of nights) counts.set(pick(night), (counts.get(pick(night)) ?? 0) + 1)
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
}

/**
 * A night, as one line.
 *
 * Four tracks that hold their width whatever is in them — picture, when, what, where —
 * with a marker column on the right for the one thing that is true of *this* row and not
 * the others. Fixed tracks rather than flex, because the thing a reader does with a list
 * like this is run their eye down one column, and a ragged one cannot be run down.
 */
function Row({ night, today }: { night: Night; today: string }) {
  const soon = today && night.night >= today ? howSoon(night.night, today) : null
  // The same step-down every card on the site uses, and this row had neither half of it.
  // Ticketmaster names variants in the path and has none generated for about a tenth of its
  // assets, so the sized URL the server built answers 403 while the original answers 200.
  const art = useArtwork(night.image_url ?? null)
  return (
    <li className={s.row}>
      <span className={s.thumb}>
        {/* Never empty. A gig with no artwork, or one whose artwork refuses, drew nothing
            and left a grey hole in a column of pictures — the bubble is what every card on
            this site falls back to, keyed on the gig so it is the same one each time. */}
        <img
          src={art.src && !art.broken ? art.src : bubbleFor(night.id)}
          alt=""
          loading="lazy"
          decoding="async"
          onError={art.onError}
        />
      </span>
      <span className={s.when}>
        <b>{WHEN.format(new Date(night.starts_at))}</b>
        {night.time_known ? <i>{TIME.format(new Date(night.starts_at))}</i> : null}
      </span>
      <AppLink className={s.what ?? ''} to={`/e/${night.id}`}>
        <b>{night.title}</b>
        {night.bill > 1 ? (
          <small>
            {night.festival ? `${night.festival} · ` : ''}
            {night.bill} acts
          </small>
        ) : night.festival ? (
          <small>{night.festival}</small>
        ) : null}
      </AppLink>
      <span className={s.where}>{night.venue}</span>
      <span className={s.mark}>
        {night.lifecycle_status !== 'scheduled' ? (
          <b className={s.dead}>{night.lifecycle_status}</b>
        ) : soon && soon !== 'tonight' && soon !== 'tomorrow' ? null : soon ? (
          soon
        ) : null}
      </span>
    </li>
  )
}

/**
 * The next night, given the room the rest of the list does not get.
 *
 * One gig at a time is the one you are about to go to, and on a page that is otherwise a
 * ledger it is the only row anybody has to act on. The artwork is the whole point of the
 * size — at row scale it is a 40px thumbnail nobody reads.
 */
function NextUp({ night, today }: { night: Night; today: string }) {
  const art = useArtwork(night.image_url ?? null)
  return (
    <AppLink className={s.next ?? ''} to={`/e/${night.id}`}>
      {/* Always drawn, because the artwork is the whole point of the size here — a 403 left
          the one row on this page anybody has to act on with a large empty box where the
          picture goes. A bubble is a picture; nothing is a fault. */}
      <span className={s.nextArt}>
        <img
          src={art.src && !art.broken ? art.src : bubbleFor(night.id)}
          alt=""
          decoding="async"
          onError={art.onError}
        />
      </span>
      <span className={s.nextSay}>
        <span className={s.mono}>
          {WHEN.format(new Date(night.starts_at))}
          {night.time_known ? ` · ${TIME.format(new Date(night.starts_at))}` : ''}
          {today ? ` · ${howSoon(night.night, today)}` : ''}
        </span>
        {/* The brand above the act, the way the cards do it. A day of All Points East is
            an All Points East date first and a Djo date second, and the card said only the
            second — which is the half a reader already knows. */}
        {night.festival ? <u className={s.brand}>{night.festival}</u> : null}
        <b>{night.title}</b>
        <span className={s.nextWhere}>
          {night.venue}
          {night.bill > 1 ? ` · ${night.bill} acts` : ''}
        </span>
      </span>
    </AppLink>
  )
}

/**
 * A section heading and, on the right, what the section amounts to.
 *
 * The count moved out of the heading and into a muted line beside it: "COMING UP 9" reads
 * as a heading with a number stuck to it, and the number is a fact about the list rather
 * than part of its name.
 */
function Head({ children, meta }: { children: string; meta?: string | null }) {
  return (
    <div className={s.sechead}>
      <SectionHeading>{children}</SectionHeading>
      {meta ? <span className={s.meta}>{meta}</span> : null}
    </div>
  )
}

function Nights({ nights, empty, today = '' }: { nights: Night[]; empty: string; today?: string }) {
  if (nights.length === 0) return <p className={s.none}>{empty}</p>
  return (
    <ul className={s.rows}>
      {nights.map(night => (
        <Row key={night.id} night={night} today={today} />
      ))}
    </ul>
  )
}

/**
 * A figure and what it counts.
 *
 * `unit` is the half of the value that is not a number — the month against a day. It is set
 * small beside the figure rather than at figure size, because a date set as a numeral is
 * four times the width of a numeral and takes the row apart: "Wed 19 Aug" wrapped onto two
 * lines and pushed everything else off the line.
 */
function Figure({
  n,
  unit,
  one,
  many,
  label,
}: {
  n: number | string
  unit?: string
  /** The label when the figure is exactly 1, and when it is not. */
  one?: string
  many?: string
  /** For a figure that is not a count — a date — and so has no plural. */
  label?: string
}) {
  // The label agrees with the number above it. "1" over "NIGHTS ON RECORD" is the same
  // mistake as "1 nights" in a sentence; it is just quieter, because a label reads as a
  // fixed heading until you notice the number is one.
  const said = label ?? (n === 1 ? one : many) ?? ''
  return (
    <div className={s.figure}>
      <span className={s.value}>
        <b>{n}</b>
        {unit ? <i>{unit}</i> : null}
      </span>
      <span className={s.mono}>{said}</span>
    </div>
  )
}

/** "1 night", not "1 nights". */
function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'Europe/London' })
const YEAR = new Intl.DateTimeFormat('en-GB', { year: '2-digit', timeZone: 'Europe/London' })

/**
 * When somebody joined, as a figure.
 *
 * This was the first night on the record, and that is a moving number: it is the earliest
 * gig anybody has got round to entering, so it walks backwards every time older history is
 * backfilled — and backfilling it is already on the list. When you joined does not move.
 *
 * The month is the figure and the year is the unit, which is the other half of what was
 * wrong with it. As a night it was "19" at figure size with "Aug" at 0.44em beside it, and
 * a three-letter month set at twenty pixels against a forty-six pixel numeral is not a word
 * anybody reads — it is a mark after the number. There is no numeral here worth that place.
 *
 * And the year is always drawn. The night it replaced showed one only when it was not this
 * year, so a date from a previous year and a date from this one were indistinguishable —
 * the same fault that had already been fixed in the briefing's dates.
 *
 * Two digits, elided. Nobody joined this in 1926 and nobody will read the "20" — it is two
 * characters of a five-column row saying something the reader already knows. The apostrophe
 * is doing the work the missing digits used to: without it "Aug 26" is a date in August.
 */
function joined(when: string): { n: string; unit: string } {
  const day = new Date(when)
  return { n: MONTH.format(day), unit: `’${YEAR.format(day)}` }
}

/**
 * Somebody's page.
 *
 * A record: what somebody has seen, and what they are about to. Two layouts were designed
 * and this is the one that was chosen; the other is gone rather than left behind a flag,
 * because a second layout nobody looks at is a second place for the privacy rules to drift.
 */
export function Profile() {
  const { username = '' } = useParams()
  const [person, setPerson] = useState<Person | null>(null)
  const [missing, setMissing] = useState(false)
  const [editing, setEditing] = useState(false)

  const load = () => {
    api
      .person(username)
      .then(setPerson)
      .catch(() => setMissing(true))
  }
  useEffect(load, [username])

  if (missing) return <main className={s.wrap}><h1 className={s.name}>No such person.</h1></main>
  if (!person) return <main className={s.wrap} aria-busy="true" />

  // The server's answer, not a guess from empty lists. Inferring it from emptiness showed a
  // locked door to an accepted friend who simply had not been anywhere yet.
  const hidden = !person.canSeeNights
  const next = person.upcoming[0]
  // Tonight, from the server. It was `next?.night` — the date of the next gig — so the
  // countdown measured every night against the next one rather than against today, and the
  // next gig read "tonight" and the one after it "tomorrow" whatever week they were in.
  const today = person.today
  const rooms = tally(person.past, n => n.venue)

  return (
    <main className={s.wrap}>
      <header className={s.head}>
        <Avatar accountId={person.id} name={person.name} has={person.hasAvatar} />
        <div className={s.who}>
          <h1 className={s.name}>{person.name}</h1>
          <p className={s.handle}>
            @{person.username}
            {person.keepsTheCalendar ? <span className={s.keeps}>curator</span> : null}
          </p>
          {person.bio ? <p className={s.bio}>{person.bio}</p> : null}
          {person.isYou ? (
            <div className={s.mine}>
              <AvatarPicker accountId={person.id} onDone={load} />
              <button type="button" className={s.pick} onClick={() => setEditing(v => !v)}>
                {editing ? 'Done' : 'Edit name'}
              </button>
              <button
                type="button"
                className={s.pick}
                onClick={() => {
                  // POST, then a full load rather than a route change: signing out changes
                  // who every page on the site is for, and the cheapest way to be sure
                  // nothing is left holding the last person's rows is to start again.
                  void fetch('/logout', { method: 'POST' }).then(() => {
                    window.location.href = '/'
                  })
                }}
              >
                Sign out
              </button>
            </div>
          ) : null}
          {editing && person.isYou ? <EditSelf person={person} onDone={() => { setEditing(false); load() }} /> : null}
        </div>

      </header>

      {hidden ? (
        <p className={s.gated}>
          You can see who {person.name} is. Where they are going is for friends — ask, and it
          is theirs to answer.
        </p>
      ) : (
        <>
          {/* The sentence that used to sit here — "2 nights on record, 2 venues. 9 more
              already planned." — said all three of the numbers below it in a second format,
              so a reader's job was to check the two against each other. */}
          <section className={s.figures}>
            <Figure n={person.past.length} one="night on record" many="nights on record" />
            <Figure n={person.upcoming.length} one="night planned" many="nights planned" />
            <Figure n={rooms.length} one="venue visited" many="venues visited" />
            <Figure n={longestRun(person.past)} one="day in a row" many="days in a row" />
            <Figure {...joined(person.memberSince)} label="member since" />
          </section>

          <section>
            <Head meta={person.upcoming.length ? count(person.upcoming.length, 'night', 'nights') : null}>
              Coming up
            </Head>
            {next ? <NextUp night={next} today={today} /> : null}
            <Nights
              nights={person.upcoming.slice(1)}
              today={today}
              empty={next ? '' : 'Nothing booked.'}
            />
          </section>

          {person.isYou ? (
            <section>
              <Head>Friends</Head>
              <Friends />
            </section>
          ) : null}

          <section>
            <Head meta={person.past.length ? count(person.past.length, 'night', 'nights') : null}>
              Been to
            </Head>
            <Nights nights={person.past} empty="Nothing yet — the record starts here." />
          </section>

          {rooms.length ? (
            <section>
              <Head meta={count(rooms.length, 'venue', 'venues')}>Venues</Head>
              <ul className={s.tally}>
                {rooms.slice(0, 8).map(([room, n]) => (
                  <li key={room}>
                    <span>{room}</span>
                    <i style={{ width: `${(n / (rooms[0]?.[1] ?? 1)) * 100}%` }} />
                    <b className={s.mono}>{n}</b>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* The two sections a profile is actually read for. Only ever the overlap, and
              only between accepted friends — so they are simply absent rather than empty
              on your own page and on somebody you do not know. */}
          {person.bothGoing.length > 0 ? (
            <section>
              <Head meta={count(person.bothGoing.length, 'night', 'nights')}>
                You&rsquo;re both going
              </Head>
              <Nights nights={person.bothGoing} empty="" />
            </section>
          ) : null}

          {person.bothLiked.length > 0 ? (
            <section>
              <Head meta={count(person.bothLiked.length, 'gig', 'gigs')}>
                You&rsquo;ve both liked
              </Head>
              <Nights nights={person.bothLiked} empty="" />
            </section>
          ) : null}

        </>
      )}
    </main>
  )
}

function EditSelf({ person, onDone }: { person: Person; onDone: () => void }) {
  const [name, setName] = useState(person.name)
  const [bio, setBio] = useState(person.bio ?? '')
  const [busy, setBusy] = useState(false)

  return (
    <form
      className={s.edit}
      onSubmit={e => {
        e.preventDefault()
        setBusy(true)
        void api.me({ name, bio }).then(onDone).finally(() => setBusy(false))
      }}
    >
      <label>
        <span className={s.mono}>Name</span>
        <input value={name} onChange={e => setName(e.target.value)} maxLength={60} />
      </label>
      <label>
        <span className={s.mono}>One line</span>
        <input
          value={bio}
          placeholder="Mostly guitars and the odd warehouse."
          onChange={e => setBio(e.target.value)}
          maxLength={160}
        />
      </label>
      <button type="submit" className={s.save} disabled={busy}>
        {busy ? 'Saving…' : 'Save'}
      </button>
    </form>
  )
}
